import { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { AlertTriangle, CheckCircle2, ExternalLink } from 'lucide-react';
import {
  Badge,
  Button,
  Card,
  CardHeader,
  ConfirmModal,
  CopyButton,
  Input,
  Modal,
  ProviderLogo,
  getStatusVariant,
} from '@/components/ui';
import { useToast } from '@/components/ui/Toast';
import { useAuth } from '@/store/auth-context';
import { routes } from '@/lib/routes';
import {
  useDeleteOrgMcpServer,
  useOrgMcpServer,
  useSyncOrgMcpServer,
  usePutOrgMcpServiceAccountCredential,
} from '@/hooks/useOrgMcpServers';
import {
  useInvalidateOrgMcpAuthStatus,
  useOrgMcpAuthStatus,
} from '@/hooks/useOrgMcpAuthStatus';
import { useMcpAuthEvents } from '@/hooks/useMcpAuthEvents';
import { useMcpProviderById } from '@/hooks/useMcpProviders';
import { useOrgCredentials } from '@/hooks/useIdentityCredentials';
import { deriveMcpStatus } from '@/types/mcp';
import type { McpSyncResult, OrgServiceAccountCredential } from '@/types/mcp';
import BackLink from '@/components/navigation/BackLink';
import { CimdClientBadge } from '@/pages/tools/mcp/CimdClientBadge';

function parseScopes(raw: string): string[] | undefined {
  const list = raw
    .split(/[,\s]+/)
    .map((s) => s.trim())
    .filter(Boolean);
  return list.length > 0 ? list : undefined;
}

export default function OrgMcpServerDetail() {
  const { serverId } = useParams<{ serverId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const orgId = user?.orgId ?? '';
  const { success, error: showError } = useToast();

  const returnUrl = `${window.location.origin}/oauth/connected`;

  const { data: server, isLoading } = useOrgMcpServer(serverId);
  // The OAuth app lives in optr-identity (a papp row keyed by serverId), not
  // on the agent-core server config — the credentials response is the only
  // place that knows which client is wired up.
  const { data: credentials } = useOrgCredentials();
  const oauthApp = credentials?.providers.find(
    (p) => p.providerInstanceId === serverId,
  );
  const catalogEntry = useMcpProviderById(server?.providerId);
  const { data: auth, refetch: refetchAuth } = useOrgMcpAuthStatus(
    serverId,
    returnUrl,
  );
  const invalidateAuth = useInvalidateOrgMcpAuthStatus();
  const syncServer = useSyncOrgMcpServer();
  const deleteServer = useDeleteOrgMcpServer();
  const putSaCredential = usePutOrgMcpServiceAccountCredential();

  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [syncResult, setSyncResult] = useState<McpSyncResult | null>(null);
  const [saModalOpen, setSaModalOpen] = useState(false);
  const [saClientId, setSaClientId] = useState('');
  const [saClientSecret, setSaClientSecret] = useState('');
  const [saRefreshToken, setSaRefreshToken] = useState('');
  const [saScopesRaw, setSaScopesRaw] = useState('');

  // The platform-event payload has no scope field — `useMcpAuthEvents` only
  // invalidates the space-keyed query (no-op when no SpaceProvider is around).
  // We invalidate the org-keyed query explicitly in the onConnected callback.
  useMcpAuthEvents({
    serverId,
    onConnected: (id) => {
      success('Connected to MCP server');
      invalidateAuth(id);
    },
    onFailed: (_id, message) => {
      showError(message ?? 'Connection failed');
    },
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600" />
      </div>
    );
  }

  if (!server) {
    return (
      <div className="text-center py-12 text-muted-foreground">
        Organization MCP server not found.{' '}
        <Link to={routes.console.org.mcpServers} className="text-primary underline">
          Back to organization MCP servers
        </Link>
      </div>
    );
  }

  // Stash the (orgId, serverId) pair so /oauth/connected can name the server.
  const openAuthorizationUrl = (url: string) => {
    try {
      localStorage.setItem(
        'optr.oauth.pending',
        JSON.stringify({
          orgId,
          serverId: server.serverId,
          ts: Date.now(),
        }),
      );
    } catch {
      // localStorage disabled — landing page falls back to generic copy.
    }
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const handleSync = async () => {
    setSyncResult(null);
    try {
      const result = await syncServer.mutateAsync({
        serverId: server.serverId,
        returnUrl,
      });
      setSyncResult(result);
      if (result.authorizationRequired && result.authorizationUrl) {
        openAuthorizationUrl(result.authorizationUrl);
        return;
      }
      success(
        `Sync complete — ${result.imported} imported, ${result.updated} updated, ${result.removed} removed`,
      );
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Sync failed');
    }
  };

  const handleDelete = async () => {
    try {
      await deleteServer.mutateAsync(server.serverId);
      success('Organization MCP server deleted');
      navigate(routes.console.org.mcpServers);
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to delete server');
    }
  };

  const handleConnect = () => {
    if (auth?.authorizationUrl) openAuthorizationUrl(auth.authorizationUrl);
  };

  const handleCheckAgain = () => {
    invalidateAuth(server.serverId);
    refetchAuth();
  };

  const openSaModal = () => {
    setSaClientId('');
    setSaClientSecret('');
    setSaRefreshToken('');
    setSaScopesRaw((server.serviceAccount?.scopes ?? []).join(', '));
    setSaModalOpen(true);
  };

  const handleSaSave = async () => {
    if (!saClientId.trim() || !saClientSecret.trim()) {
      showError('Client ID and client secret are required.');
      return;
    }
    const credential: OrgServiceAccountCredential = {
      clientId: saClientId.trim(),
      clientSecret: saClientSecret.trim(),
      refreshToken: saRefreshToken.trim() || undefined,
      scopes: parseScopes(saScopesRaw),
    };
    try {
      await putSaCredential.mutateAsync({ serverId: server.serverId, credential });
      success('Service-account credential saved');
      setSaModalOpen(false);
    } catch (err) {
      showError(
        err instanceof Error ? err.message : 'Failed to save credential',
      );
    }
  };

  return (
    <div className="space-y-6">
      <BackLink to={routes.console.org.mcpServers}>Organization MCP servers</BackLink>

      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          {server.providerId && (
            <ProviderLogo
              slug={server.providerId}
              displayName={catalogEntry?.displayName}
              className="h-12 w-12"
              fallbackClassName="text-sm"
            />
          )}
          <div>
            <h1 className="text-2xl font-bold text-foreground">{server.name}</h1>
            {catalogEntry && (
              <p className="text-xs text-muted-foreground mt-0.5">
                {catalogEntry.displayName}
              </p>
            )}
          </div>
        </div>
        {(() => {
          const status = deriveMcpStatus(server);
          return <Badge variant={getStatusVariant(status)}>{status}</Badge>;
        })()}
      </div>

      {/* Callback URL — only meaningful for OAuth installs that actually
          do the redirect dance. */}
      {server.authMode !== 'api_key' &&
        server.authMode !== 'oauth_manual' &&
        server.callbackUrl.trim().length > 0 && (
          <Card>
            <CardHeader
              title="Callback URL"
              description="If you supplied your own OAuth client, add this as an allowed redirect URI on your OAuth app. (Servers that support Dynamic Client Registration don't need this.)"
            />
            <div className="flex items-center gap-3">
              <code className="text-sm bg-muted px-3 py-2 rounded-md flex-1 font-mono break-all">
                {server.callbackUrl}
              </code>
              <CopyButton value={server.callbackUrl} label="Copy" size="md" />
            </div>
          </Card>
        )}

      <div className="grid gap-6 md:grid-cols-2">
        <Card>
          <CardHeader
            title="Configuration"
            action={
              <Link to={routes.console.org.mcpServerEdit(server.serverId)}>
                <Button variant="outline" size="sm">
                  Edit
                </Button>
              </Link>
            }
          />
          <dl className="space-y-3">
            <div>
              <dt className="text-sm font-medium text-muted-foreground">
                Server URL
              </dt>
              <dd className="mt-0.5 text-sm text-foreground break-all">
                {server.serverUrl}
              </dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-muted-foreground">
                Default group
              </dt>
              <dd className="mt-0.5 text-sm text-foreground">
                {server.defaultGroup || '—'}
              </dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-muted-foreground">Scopes</dt>
              <dd className="mt-0.5 text-sm text-foreground">
                {server.scopes && server.scopes.length > 0
                  ? server.scopes.join(', ')
                  : '—'}
              </dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-muted-foreground">
                OAuth client
              </dt>
              <dd className="mt-0.5 text-sm text-foreground">
                {oauthApp?.oauthApp ? (
                  <>
                    <span className="font-mono text-xs">
                      {oauthApp.oauthApp.clientId}
                    </span>
                    {!oauthApp.oauthApp.hasClientSecret && (
                      <span className="ml-2 text-xs text-muted-foreground">
                        public client — no secret
                      </span>
                    )}
                  </>
                ) : server.oauthClientMode === 'cimd' ? (
                  <CimdClientBadge />
                ) : (
                  'Not configured'
                )}
              </dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-muted-foreground">
                Server ID
              </dt>
              <dd className="mt-0.5 text-sm text-foreground font-mono text-xs">
                {server.serverId}
              </dd>
            </div>
            {server.lastError && (
              <div>
                <dt className="text-sm font-medium text-muted-foreground">
                  Last error
                </dt>
                <dd className="mt-0.5 text-sm text-red-600 dark:text-red-400">
                  {server.lastError}
                </dd>
              </div>
            )}
          </dl>
        </Card>

        <Card>
          <CardHeader
            title="Service account"
            description="Organization-level fallback credential — used as Tier 3 when a calling user has no personal token."
            action={
              <Button variant="outline" size="sm" onClick={openSaModal}>
                {server.serviceAccount?.configured
                  ? 'Replace credential'
                  : 'Add credential'}
              </Button>
            }
          />
          {server.serviceAccount?.configured ? (
            <div className="space-y-3">
              <div className="flex items-center gap-2 text-sm text-green-700 dark:text-green-300">
                <CheckCircle2 className="h-5 w-5" />
                <span className="font-medium">Configured</span>
              </div>
              {server.serviceAccount.scopes &&
                server.serviceAccount.scopes.length > 0 && (
                  <p className="text-sm text-muted-foreground">
                    Scopes: {server.serviceAccount.scopes.join(', ')}
                  </p>
                )}
              {server.serviceAccount.lastError && (
                <div className="flex gap-2 text-sm text-yellow-700 dark:text-yellow-300">
                  <AlertTriangle className="h-4 w-4 flex-shrink-0 mt-0.5" />
                  <p>{server.serviceAccount.lastError}</p>
                </div>
              )}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">
              No service-account credential set. Tool calls fall back to
              per-user OAuth only.
            </p>
          )}
        </Card>
      </div>

      <Card>
        <CardHeader
          title="Your connection"
          description="Each user can also connect for themselves — agents you own use your personal connection before falling back to the organization service account."
        />
        {auth?.authorized ? (
          <div className="space-y-3">
            <div className="flex items-center gap-2 text-sm text-green-700 dark:text-green-300">
              <CheckCircle2 className="h-5 w-5" />
              <span className="font-medium">Connected</span>
            </div>
            {auth.scopes && auth.scopes.length > 0 && (
              <p className="text-sm text-muted-foreground">
                Scopes: {auth.scopes.join(', ')}
              </p>
            )}
            {auth.expiresAt && (
              <p className="text-xs text-muted-foreground">
                Renews automatically • token expires{' '}
                {new Date(auth.expiresAt).toLocaleString()}
              </p>
            )}
          </div>
        ) : (
          <div className="space-y-3">
            {auth?.lastError && (
              <div className="flex gap-2 text-sm text-yellow-700 dark:text-yellow-300">
                <AlertTriangle className="h-4 w-4 flex-shrink-0 mt-0.5" />
                <p>{auth.lastError}</p>
              </div>
            )}
            <p className="text-sm text-muted-foreground">
              Connect with your own credentials. The authorization page opens
              in a new tab.
            </p>
            <div className="flex items-center gap-2">
              <Button
                onClick={handleConnect}
                disabled={!auth?.authorizationUrl}
              >
                Connect {server.name}
                <ExternalLink className="h-3.5 w-3.5 ml-2" />
              </Button>
              <Button variant="outline" onClick={handleCheckAgain}>
                Check again
              </Button>
            </div>
          </div>
        )}
      </Card>

      <Card>
        <CardHeader
          title="Tools"
          description="Pull tools from the MCP server into the organization tool catalog. Synced tools are available in every space."
          action={
            <Button onClick={handleSync} disabled={syncServer.isPending}>
              {syncServer.isPending ? 'Syncing…' : 'Sync tools'}
            </Button>
          }
        />
        {syncResult ? (
          syncResult.authorizationRequired ? (
            <div className="rounded-md p-3 text-sm bg-yellow-50 border border-yellow-200 text-yellow-800 dark:bg-yellow-900/20 dark:border-yellow-800 dark:text-yellow-300">
              <p className="font-medium">Authorization required</p>
              <p className="mt-1">
                We opened the consent page in a new tab. Finish connecting and
                the sync will succeed on the next attempt.
              </p>
            </div>
          ) : (
            <div className="space-y-2 text-sm">
              <p className="text-foreground">
                {syncResult.imported} imported · {syncResult.updated} updated ·{' '}
                {syncResult.skipped} skipped · {syncResult.removed} removed
              </p>
              {syncResult.errors.length > 0 && (
                <ul className="text-sm text-red-600 dark:text-red-400 list-disc list-inside">
                  {syncResult.errors.map((err, i) => (
                    <li key={i}>
                      {err.tool ? `${err.tool}: ` : ''}
                      {err.message}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )
        ) : server.lastSyncedAt ? (
          <p className="text-sm text-muted-foreground">
            Last synced {new Date(server.lastSyncedAt).toLocaleString()}
          </p>
        ) : (
          <p className="text-sm text-muted-foreground">Not synced yet.</p>
        )}
      </Card>

      <Card>
        <CardHeader title="Danger Zone" description="Irreversible actions" />
        <Button variant="destructive" onClick={() => setDeleteModalOpen(true)}>
          Delete organization MCP server
        </Button>
      </Card>

      <ConfirmModal
        isOpen={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        onConfirm={handleDelete}
        title="Delete organization MCP server"
        message={`Are you sure you want to delete "${server.name}"? This removes its tools from every space in the organization and revokes all per-user connections.`}
        confirmText="Delete"
        isLoading={deleteServer.isPending}
      />

      <Modal
        isOpen={saModalOpen}
        onClose={() => setSaModalOpen(false)}
        title={
          server.serviceAccount?.configured
            ? 'Replace service-account credential'
            : 'Add service-account credential'
        }
        footer={
          <>
            <Button
              variant="outline"
              onClick={() => setSaModalOpen(false)}
              disabled={putSaCredential.isPending}
            >
              Cancel
            </Button>
            <Button onClick={handleSaSave} isLoading={putSaCredential.isPending}>
              Save credential
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <p className="text-sm text-muted-foreground">
            Used as the organization-level fallback when a calling user has no
            personal token for this install. The previous credential, if any,
            is replaced.
          </p>
          <Input
            label="Client ID"
            value={saClientId}
            onChange={(e: { target: { value: string } }) =>
              setSaClientId(e.target.value)
            }
            placeholder="OAuth client id"
            autoComplete="off"
          />
          <Input
            label="Client secret"
            type="password"
            value={saClientSecret}
            onChange={(e: { target: { value: string } }) =>
              setSaClientSecret(e.target.value)
            }
            placeholder="OAuth client secret"
            autoComplete="new-password"
          />
          <Input
            label="Refresh token"
            type="password"
            value={saRefreshToken}
            onChange={(e: { target: { value: string } }) =>
              setSaRefreshToken(e.target.value)
            }
            placeholder="Optional"
            autoComplete="new-password"
            hint="Provide if the SA's access shouldn't depend on user OAuth."
          />
          <Input
            label="Scopes"
            value={saScopesRaw}
            onChange={(e: { target: { value: string } }) =>
              setSaScopesRaw(e.target.value)
            }
            placeholder="Optional — comma- or space-separated"
            hint="Scopes the SA token should carry."
          />
        </div>
      </Modal>
    </div>
  );
}
